// Protegrity IT Onboarding — pure GUIDANCE checklist with STEP-BY-STEP instructions
// Each section has:
//   - agentScript: what the agent says (voice-ready)
//   - mainLink: the primary URL/portal for this section
//   - steps: numbered actions user follows
//   - notes: important reminders shown at bottom
//   - items: legacy IT staff checklist (kept for IT staff reference)

export const CHECKLIST = [
  // ===========================================================================
  // SECTION 1 — Welcome & Shipping Box
  // ===========================================================================
  {
    id: 1,
    title: "Welcome & Shipping Box",
    icon: "📦",
    agentScript: "Hi and welcome to Protegrity! Before we begin, one quick reminder — please keep your laptop shipping box safe in case we need to ship the device back in the future. Let me know when you're ready to start.",
    mainLink: null,
    steps: [
      {
        id: "1-s1",
        text: "Find your laptop shipping box",
        detail: "The cardboard box your laptop arrived in",
        os: "all"
      },
      {
        id: "1-s2",
        text: "Store it somewhere safe at home or in your desk",
        detail: "We may need it for future shipping or returns",
        os: "all"
      }
    ],
    notes: [],
    items: [
      { id: "1-1", text: "Remind new hire to keep the laptop shipping box for future use", os: "all" },
    ],
    links: []
  },

  // ===========================================================================
  // SECTION 2 — Change Password
  // ===========================================================================
  {
    id: 2,
    title: "Change Password",
    icon: "🔑",
    agentScript: "Now let's change your initial password. One important update first: the Protegrity password policy has changed. Your new password must be at least 12 characters long. All the other rules stay the same. I'll guide you through it step by step. First, click the link below to open the Microsoft 365 portal. I'll wait until you're signed in.",
    mainLink: {
      label: "Open Microsoft 365 Portal",
      url: "https://m365.cloud.microsoft/"
    },
    steps: [
      {
        id: "2-s1",
        text: "Click the link above to open Microsoft 365 portal",
        detail: "A new browser tab will open with the M365 sign-in page",
        os: "all"
      },
      {
        id: "2-s2",
        text: "Sign in using your Protegrity email and current password",
        detail: "Use the temporary password provided by IT in your welcome email",
        os: "all"
      },
      {
        id: "2-s3",
        text: "Click your profile icon at the bottom-left corner",
        detail: "Look for the circle with your initials in the bottom-left of the screen",
        os: "all"
      },
      {
        id: "2-s4",
        text: "Click 'View account' from the menu",
        detail: "This opens your Microsoft account page in a new tab",
        os: "all"
      },
      {
        id: "2-s5",
        text: "On the left-hand side, click 'Change password'",
        detail: "You'll see options like Security info, Devices, Password — click Password",
        os: "all"
      },
      {
        id: "2-s6",
        text: "Enter your current password, then your new password twice",
        detail: "Your new password must be at least 12 characters — this is the updated Protegrity policy. Microsoft will show the rules on screen.",
        os: "all",
        rules: [
          "At least 12 characters long (new policy)",
          "At least 1 number (0-9)",
          "At least 1 uppercase letter (A-Z)",
          "At least 1 lowercase letter (a-z)",
          "At least 1 special character like ! @ # $ % ^ & *",
          "Tip: a passphrase such as Coffee&Rain2026! (16 characters) is easier to remember than a random string"
        ]
      },
      {
        id: "2-s7",
        text: "Click Submit to save your new password",
        detail: "You'll see a confirmation message. You may need to sign in again with the new password.",
        os: "all"
      },
      {
        id: "2-s8",
        text: "For Mac users — watch for the Iru password sync prompt",
        detail: "Within 5 minutes, an Iru popup will ask you to confirm the new password. Enter it to keep your Mac in sync.",
        os: "mac"
      }
    ],
    notes: [
      "🆕 New policy — every new password must be at least 12 characters",
      "✅ An existing password keeps working until the next change or the 90-day rotation",
      "🔁 Password must be changed every 90 days — reminder emails start 10 days before",
      "⏱️ You cannot change your password more than once in 24 hours",
      "🔒 Password history and lockout thresholds are unchanged — you can't reuse recent passwords",
      "🔄 A new password syncs across all Protegrity systems within 1–2 minutes"
    ],
    items: [
      { id: "2-1", text: "User signed in to M365 portal", os: "all" },
      { id: "2-2", text: "User changed password successfully", os: "all" },
      { id: "2-3", text: "Iru sync prompt explained", os: "mac" },
    ],
    links: [
      { label: "Password change KB article", url: "https://ithelp.protegrity.com/app/itdesk/ui/solutions/98954000001856031/details" },
    ]
  },

  // ===========================================================================
  // SECTION 3 — Configure MFA
  // ===========================================================================
  {
    id: 3,
    title: "Configure & Explain MFA",
    icon: "🔐",
    agentScript: "Next, let's set up Multi-Factor Authentication, or MFA. This adds an extra layer of security to your account. Before we start, please download the Microsoft Authenticator app on your phone. I'll wait while you do that.",
    mainLink: null,
    steps: [
      {
        id: "3-s1",
        text: "On your phone, open the App Store (iPhone) or Google Play Store (Android)",
        detail: "Use your personal mobile device",
        os: "all"
      },
      {
        id: "3-s2",
        text: "Search for 'Microsoft Authenticator' and install it",
        detail: "Look for the blue icon with a white lock symbol",
        os: "all"
      },
      {
        id: "3-s3",
        text: "On your computer, go back to the 'View account' page",
        detail: "Same page you used for password change (myaccount.microsoft.com)",
        os: "all"
      },
      {
        id: "3-s4",
        text: "On the left-hand side, click 'Security info'",
        detail: "Below Password option you'll see Security info",
        os: "all"
      },
      {
        id: "3-s5",
        text: "Click '+ Add sign-in method'",
        detail: "Top of the Security info page",
        os: "all"
      },
      {
        id: "3-s6",
        text: "Select 'Authenticator app' from the dropdown, then click Add",
        detail: "A QR code will be displayed on screen",
        os: "all"
      },
      {
        id: "3-s7",
        text: "On your phone, open the Microsoft Authenticator app",
        detail: "Tap '+' or 'Add account' button",
        os: "all"
      },
      {
        id: "3-s8",
        text: "Choose 'Work or school account' → 'Scan a QR code'",
        detail: "Your phone camera will activate",
        os: "all"
      },
      {
        id: "3-s9",
        text: "Point your phone camera at the QR code on your computer screen",
        detail: "The account will be added automatically",
        os: "all"
      },
      {
        id: "3-s10",
        text: "Click Next on your computer and complete the verification",
        detail: "You'll get a test notification on your phone — approve it",
        os: "all"
      },
      {
        id: "3-s11",
        text: "Change default sign-in method to 'Authenticator app notification'",
        detail: "At the top of Security info page, click 'Change' next to 'Default sign-in method' → select Microsoft Authenticator notification",
        os: "all"
      }
    ],
    notes: [
      "📱 Keep Microsoft Authenticator installed — you'll use it for every sign-in",
      "🔔 You'll receive a notification on your phone to approve each sign-in",
      "🆘 If you lose your phone, contact IT immediately to reset MFA",
      "💻 Mac users: MFA at the user level is disabled — device-level security covers you"
    ],
    items: [
      { id: "3-1", text: "Authenticator app installed on phone", os: "all" },
      { id: "3-2", text: "MFA configured via Security info", os: "all" },
      { id: "3-3", text: "Default sign-in method changed to Authenticator", os: "all" },
      { id: "3-4", text: "Test sign-in verified working", os: "all" },
    ],
    links: []
  },

  // ===========================================================================
  // SECTIONS 4-14 — Placeholder structure (we'll fill these next)
  // ===========================================================================
  {
    id: 4,
    title: "Disk Encryption",
    icon: "🛡️",
    agentScript: "Your laptop's hard drive is fully encrypted to protect your data if the laptop is ever lost or stolen. Let me explain how this affects your daily sign-in experience.",
    mainLink: null,
    steps: [
      { id: "4-s1", text: "Encryption is already configured by IT — no action needed", detail: "BitLocker is active on Windows machines", os: "win" },
      { id: "4-s2", text: "Encryption is already configured by IT — no action needed", detail: "FileVault is active on Mac machines", os: "mac" },
      { id: "4-s3", text: "For Mac sign-in flow: First you'll see the FileVault screen (only password needed)", detail: "This is the disk encryption unlock screen", os: "mac" },
      { id: "4-s4", text: "After FileVault, you'll see the Iru password screen", detail: "Here you need to enter both full email and password", os: "mac" }
    ],
    notes: [
      "🔒 If you forget your password, IT can help recover access — don't try to remove encryption yourself",
      "💾 All data on the drive is automatically encrypted — no extra action needed from you"
    ],
    items: [
      { id: "4-1", text: "Confirm BitLocker is active", os: "win" },
      { id: "4-2", text: "Confirm FileVault is active", os: "mac" },
      { id: "4-3", text: "Mac sign-in flow explained", os: "mac" }
    ],
    links: []
  },

  // Remaining sections (5-14) — to be filled with step-by-step format
  // These are placeholders using the old format for now
  {
    id: 5,
    title: "Office 365 & My Apps",
    icon: "🌐",
    agentScript: "Let me show you the Office 365 portal where you'll manage your account, and the My Apps portal where you'll find all the applications you have access to.",
    mainLink: { label: "My Apps Portal", url: "https://myapps.microsoft.com/" },
    steps: [
      { id: "5-s1", text: "Click 'My Apps Portal' link above to see all apps assigned to you", detail: "This is your one-stop access page", os: "all" },
      { id: "5-s2", text: "Click any app tile to launch it (e.g., Outlook, Teams, OneDrive)", detail: "First click may ask you to sign in with MFA", os: "all" },
      { id: "5-s3", text: "Bookmark this page for easy access later", detail: "Save myapps.microsoft.com in your browser favorites", os: "all" }
    ],
    notes: [
      "💡 Pro tip: Pin the My Apps portal to your bookmarks bar for quick access"
    ],
    items: [
      { id: "5-1", text: "Walk through Office 365 portal", os: "all" },
      { id: "5-2", text: "Show My Apps portal", os: "all" },
    ],
    links: [
      { label: "Microsoft 365 Portal", url: "https://m365.cloud.microsoft/" }
    ]
  },

  {
    id: 6,
    title: "Outlook & Teams",
    icon: "📧",
    agentScript: "Outlook is your email client and Teams is where we collaborate, message, and have meetings like this one. Both should already be installed on your laptop. Let me show you how to access them.",
    mainLink: null,
    steps: [
      { id: "6-s1", text: "Find the Outlook icon in your taskbar or applications", detail: "It's a blue envelope icon", os: "all" },
      { id: "6-s2", text: "Open Outlook and sign in with your Protegrity email", detail: "First sign-in will trigger MFA — approve the notification on your phone", os: "all" },
      { id: "6-s3", text: "Your inbox will start loading — this may take a few minutes the first time", detail: "Don't worry, all your future emails will appear here", os: "all" },
      { id: "6-s4", text: "Find Microsoft Teams icon and open it", detail: "Purple icon with two chat bubbles", os: "all" },
      { id: "6-s5", text: "Sign in to Teams with your Protegrity email", detail: "Same credentials as Outlook", os: "all" }
    ],
    notes: [
      "📨 All work emails come to Outlook — check it regularly",
      "💬 Teams is for instant messaging, calls, and meetings with colleagues"
    ],
    items: [
      { id: "6-1", text: "Show Outlook for email", os: "all" },
      { id: "6-2", text: "Show Teams for collaboration", os: "all" },
    ],
    links: []
  },

  {
    id: 7,
    title: "OneDrive & SharePoint",
    icon: "☁️",
    agentScript: "OneDrive automatically backs up your important folders to the cloud. SharePoint is where teams share documents. Let me explain how they work together.",
    mainLink: null,
    steps: [
      { id: "7-s1", text: "Find OneDrive in your taskbar (cloud icon)", detail: "Windows: bottom-right system tray. Mac: top-right menu bar", os: "all" },
      { id: "7-s2", text: "Sign in to OneDrive with your Protegrity email", detail: "First time setup", os: "all" },
      { id: "7-s3", text: "OneDrive automatically backs up Desktop, Documents, and Pictures folders", detail: "Anything you save in those folders syncs to the cloud", os: "all" },
      { id: "7-s4", text: "Access SharePoint via your team's site link", detail: "Your manager or team lead will share the URL", os: "all" },
      { id: "7-s5", text: "You can sync SharePoint folders to your PC via OneDrive", detail: "Click 'Sync' button on any SharePoint document library", os: "all" }
    ],
    notes: [
      "☁️ Files in OneDrive folders are backed up automatically — no manual saving needed",
      "👥 SharePoint is for team-shared files; OneDrive is for your personal work files",
      "🔄 Don't store work files only on your Desktop without OneDrive sync — they won't be backed up"
    ],
    items: [
      { id: "7-1", text: "Explain what gets backed up to OneDrive", os: "all" },
      { id: "7-2", text: "Review SharePoint", os: "all" },
      { id: "7-3", text: "Mention SharePoint sync to PC via OneDrive", os: "all" },
    ],
    links: []
  },

  {
    id: 8,
    title: "Mimecast (Email Security)",
    icon: "✉️",
    agentScript: "Mimecast filters your email for spam and phishing attempts. Sometimes it quarantines emails it thinks are suspicious. Let me show you how to manage held emails.",
    mainLink: { label: "Mimecast Web Portal", url: "https://login.mimecast.com" },
    steps: [
      { id: "8-s1", text: "You'll receive daily Mimecast digest emails", detail: "These list any emails being held for review", os: "all" },
      { id: "8-s2", text: "Click 'Release' in the digest to deliver a held email to your inbox", detail: "Or 'Block' to permanently block that sender", os: "all" },
      { id: "8-s3", text: "Open Outlook and look for the Mimecast plugin in the ribbon", detail: "You'll see Mimecast options at the top of Outlook (classic Outlook only)", os: "win" },
      { id: "8-s4", text: "On Mac, use the Mimecast web portal — the Outlook plugin doesn't work on new Outlook for Mac", detail: "Click the link above to access the web portal", os: "mac" },
      { id: "8-s5", text: "Use the Mimecast web portal for advanced features", detail: "Sign in with your Protegrity email", os: "all" }
    ],
    notes: [
      "⚠️ If you don't see an expected email, check the Mimecast digest first",
      "🚨 NEVER release emails from suspicious senders — when in doubt, report it"
    ],
    items: [
      { id: "8-1", text: "Explain Mimecast system emails", os: "all" },
      { id: "8-2", text: "Show Mimecast Outlook plugin (classic Outlook)", os: "win" },
      { id: "8-3", text: "Show Mimecast web portal", os: "all" },
    ],
    links: []
  },

  {
    id: 9,
    title: "Zscaler (VPN & Internet Security)",
    icon: "🌍",
    agentScript: "Zscaler does two things — it acts as your VPN when working remotely, and it secures your internet browsing by blocking malicious sites. Let me explain how this works.",
    mainLink: null,
    steps: [
      { id: "9-s1", text: "Zscaler is already installed on your laptop", detail: "Look for the Zscaler icon in your system tray or menu bar", os: "all" },
      { id: "9-s2", text: "Zscaler runs automatically in the background", detail: "You don't need to start it manually", os: "all" },
      { id: "9-s3", text: "If you can't access internal Protegrity sites, check Zscaler is connected", detail: "Click the Zscaler icon — it should show 'Connected'", os: "all" },
      { id: "9-s4", text: "Some external websites may be blocked", detail: "This is intentional — Zscaler blocks known malicious sites", os: "all" },
      { id: "9-s5", text: "If a legitimate site is blocked, request access via IT ticket", detail: "We'll cover the ticketing system in a later section", os: "all" }
    ],
    notes: [
      "🌐 Zscaler protects you on any network — home WiFi, coffee shops, hotels",
      "🚫 Personal social media or non-work sites may be blocked during work hours",
      "✅ When Zscaler is connected, you can access internal Protegrity resources from anywhere"
    ],
    items: [
      { id: "9-1", text: "Explain Zscaler VPN", os: "all" },
      { id: "9-2", text: "Explain Zscaler Internet Security", os: "all" },
      { id: "9-3", text: "Explain conditional access compliance policy", os: "win" },
      { id: "9-4", text: "Explain conditional access certificate", os: "mac" },
    ],
    links: []
  },

  {
    id: 10,
    title: "Phishing Reporting",
    icon: "🎣",
    agentScript: "If you ever receive a suspicious or phishing email, you can report it to our security team with one click. Let me show you the Report Phish button in Outlook.",
    mainLink: null,
    steps: [
      { id: "10-s1", text: "Open Outlook", detail: "Either desktop app or web version", os: "all" },
      { id: "10-s2", text: "Look at the top ribbon — find 'Report Phish' or 'Report Phishing' button", detail: "Usually has a fish-hook icon", os: "all" },
      { id: "10-s3", text: "When you receive a suspicious email, click on it first", detail: "Make sure the suspicious email is selected", os: "all" },
      { id: "10-s4", text: "Click 'Report Phish' to send the email to our security team", detail: "The email is moved out of your inbox and analyzed", os: "all" },
      { id: "10-s5", text: "The Information Security team will review and take action", detail: "You may be contacted if they need more details", os: "all" }
    ],
    notes: [
      "🎣 If unsure, report it — better safe than sorry",
      "⚠️ Don't click links or open attachments in suspicious emails",
      "📧 You'll occasionally get phishing training emails — these are intentional tests"
    ],
    items: [
      { id: "10-1", text: "Explain Report Phish add-in in Outlook", os: "all" },
    ],
    links: []
  },

  {
    id: 11,
    title: "IT Ticketing System",
    icon: "🎫",
    agentScript: "For any IT support, use our ticketing system at ithelp.protegrity.com. Let me show you how to submit a ticket.",
    mainLink: { label: "IT Help Portal", url: "https://ithelp.protegrity.com/" },
    steps: [
      { id: "11-s1", text: "Click the IT Help Portal link above", detail: "Opens in a new browser tab", os: "all" },
      { id: "11-s2", text: "Sign in with your Protegrity email (SSO)", detail: "MFA approval may be needed", os: "all" },
      { id: "11-s3", text: "Click 'New Request' or 'Submit a Ticket'", detail: "Usually a button in the top-right or middle of the page", os: "all" },
      { id: "11-s4", text: "Choose the right category for your issue", detail: "e.g., Hardware, Software, Access Request, Password Reset", os: "all" },
      { id: "11-s5", text: "Fill in the details — subject, description, urgency", detail: "Be specific about what's happening and when it started", os: "all" },
      { id: "11-s6", text: "Click Submit — you'll receive a ticket number by email", detail: "Track your ticket via the same portal", os: "all" }
    ],
    notes: [
      "🎫 Always submit tickets via the portal, not Teams DMs to IT staff",
      "⏱️ Standard response time is 1 business day, urgent issues are escalated",
      "📧 You'll get email updates as your ticket progresses"
    ],
    items: [
      { id: "11-1", text: "Show ticketing system", os: "all" },
      { id: "11-2", text: "Show how to submit a request", os: "all" },
    ],
    links: []
  },

  {
    id: 12,
    title: "Useful Links Page",
    icon: "🔗",
    agentScript: "We have a Useful Links page on SharePoint with quick access to common resources you'll need. Let me show you.",
    mainLink: { label: "Useful Links on SharePoint", url: "https://protegrity.sharepoint.com/Pages/Useful%20Links.aspx" },
    steps: [
      { id: "12-s1", text: "Click the Useful Links link above", detail: "Opens our SharePoint page with all key URLs", os: "all" },
      { id: "12-s2", text: "Browse the categories — HR, IT, Tools, Policies", detail: "All important Protegrity links are listed here", os: "all" },
      { id: "12-s3", text: "Bookmark this page for easy reference", detail: "Save it in your browser favorites", os: "all" }
    ],
    notes: [
      "🔖 This page is updated by IT regularly — check back if you need a link you can't remember"
    ],
    items: [
      { id: "12-1", text: "Show Useful Links page", os: "all" },
    ],
    links: []
  },

  {
    id: 13,
    title: "Mobile Device Enrollment",
    icon: "📱",
    optional: true,
    agentScript: "This last step is optional — enrolling your personal smartphone with our Company Portal app lets you access work email and Teams on your phone. Want to do it now, or shall I email you the instructions?",
    mainLink: null,
    steps: [
      { id: "13-s1", text: "On your phone, open the App Store (iPhone) or Play Store (Android)", detail: "Use your personal mobile device", os: "all" },
      { id: "13-s2", text: "Search for 'Intune Company Portal' and install it", detail: "Look for the Microsoft logo", os: "all" },
      { id: "13-s3", text: "Open Company Portal and sign in with your Protegrity email", detail: "Approve the MFA notification on your phone", os: "all" },
      { id: "13-s4", text: "Follow the enrollment wizard — accept the prompts", detail: "Your phone is being registered as a managed device", os: "all" },
      { id: "13-s5", text: "Wait for confirmation that enrollment is complete", detail: "May take 2-3 minutes", os: "all" },
      { id: "13-s6", text: "After enrollment, install work apps from Company Portal", detail: "Outlook, Teams, and others will appear in the app", os: "all" }
    ],
    notes: [
      "📱 Enrollment is optional but recommended if you want work email on your phone",
      "🔐 Your personal data stays separate — IT only manages work apps and data",
      "📧 If you skip, instructions will be emailed for later setup"
    ],
    items: [
      { id: "13-1", text: "Install Company Portal app", os: "all" },
      { id: "13-2", text: "Walk through enrollment wizard", os: "all" },
      { id: "13-3", text: "Email instructions if skipped", os: "all" },
    ],
    links: []
  },

  // ===========================================================================
  // SECTION 14 — Installing Approved Software
  // ===========================================================================
  {
    id: 14,
    title: "Installing Approved Software",
    // Shown in brackets after the title, depending on the selected device
    titleSuffix: { win: "Company Portal", mac: "Iru Self-Service" },
    icon: "📦",
    agentScript: "Now let's look at how you install software yourself. Protegrity provides a self-service portal containing software that IT has already approved and security-reviewed. On Windows this is Company Portal, and on Mac it is the Iru Self-Service app. You can install from it any time — you won't need an admin password, and you won't need to raise a ticket. Please note the Microsoft Store and the Mac App Store are blocked, so software must come from the self-service portal.",
    mainLink: null,
    steps: [
      // ---------- WINDOWS ----------
      {
        id: "14-s1",
        text: "Open Company Portal",
        detail: "This is the approved self-service portal on Windows. It is already installed on your Protegrity laptop. It is not the Microsoft Store, which is blocked on Protegrity devices.",
        os: "win",
        rulesLabel: "Where to find it:",
        rules: [
          "Click Start and type 'Company Portal'",
          "Or look in the Start menu list under C",
          "Or check the taskbar if IT has pinned it for you"
        ]
      },
      {
        id: "14-s2",
        text: "Sign in with your Protegrity email if prompted",
        detail: "Use the same account and MFA you set up earlier in this session.",
        os: "win"
      },
      {
        id: "14-s3",
        text: "Find the app you need",
        detail: "Browse Apps, use App categories, or search by name from the Home screen.",
        os: "win"
      },
      {
        id: "14-s4",
        text: "Click the app, then click Install",
        detail: "The installation runs by itself. You will not be asked for an administrator password.",
        os: "win"
      },
      {
        id: "14-s5",
        text: "Check progress under Downloads & updates",
        detail: "This page shows what is installing. The 'Required by your organization' column tells you which apps IT installs automatically versus which ones are optional for you to choose.",
        os: "win"
      },

      // ---------- MAC ----------
      {
        id: "14-s6",
        text: "Open the Iru Self-Service app",
        detail: "This is the approved self-service portal on Mac. It was installed automatically when your Mac was enrolled. It is not the Mac App Store, which is blocked on Protegrity devices. Iru was previously called Kandji, so older references may still use that name.",
        os: "mac",
        rulesLabel: "Where to find it:",
        rules: [
          "Click the icon in the menu bar at the top-right of your screen",
          "Or open Launchpad and look for Iru Self-Service",
          "Or open Finder → Applications → Iru Self-Service",
          "Or click it in the Dock if it has been added there"
        ]
      },
      {
        id: "14-s7",
        text: "Browse Recommended or All, or search by name",
        detail: "Recommended shows the apps IT suggests for your role. All shows everything available to you.",
        os: "mac"
      },
      {
        id: "14-s8",
        text: "Click Install on the app you need",
        detail: "No admin password is required. The button changes to Open once the app is installed.",
        os: "mac"
      },
      {
        id: "14-s9",
        text: "Use the ... menu for more options",
        detail: "After installing, the ... menu next to an app offers Reinstall, Show in Finder, and Add to Dock. You can queue several apps to install at once.",
        os: "mac"
      },

      // ---------- BOTH ----------
      {
        id: "14-s10",
        text: "If the application you're looking for is not available in Company Portal, check the Windows Approved or Denied Applications for Corporate Use list",
        detail: "Protegrity maintains a list of applications that Information Security has already reviewed for Windows and macOS. Open the list for your device and search for the application by name. If it is shown as approved, you may install it directly from the Internet. If it is shown as denied, it must not be installed on a Protegrity device.",
        os: "win",
        stepLinks: [
          { label: "Open the Windows approved software list", url: "https://protegrity.sharepoint.com/sites/IT/Lists/Windows%20Software%20Inventory/AllItems.aspx" }
        ]
      },
      {
        id: "14-s11",
        text: "If the application you're looking for is not available in the Iru Self-Service app, check the MacOS Approved or Denied Applications for Corporate Use list",
        detail: "Protegrity maintains a list of applications that Information Security has already reviewed for Windows and macOS. Open the list for your device and search for the application by name. If it is shown as approved, you may install it directly from the Internet. If it is shown as denied, it must not be installed on a Protegrity device.",
        os: "mac",
        stepLinks: [
          { label: "Open the macOS approved software list", url: "https://protegrity.sharepoint.com/sites/IT/Lists/MacOS%20Software%20Inventory/All%20Software.aspx" }
        ]
      },
      {
        id: "14-s12",
        text: "If installation requires administrator rights, request temporary access",
        detail: "Some approved applications ask for an administrator password during installation. You do not have permanent administrator rights, so submit a CyberArk admin privilege request and IT will grant time-limited access.",
        os: "all",
        stepLinks: [
          { label: "Raise a CyberArk admin privilege request", url: "https://ithelp.protegrity.com/app/itdesk/ui/requests/add?reqTemplate=98954000047393431" }
        ]
      },
      {
        id: "14-s13",
        text: "If you are still unable to install approved software, raise a request with IT",
        detail: "If the application does not appear on the approved list, or you are unable to install it after following the steps above, submit an Access Request Form. State which application you need and the business reason for it. Information Security will review it for security, licensing and compliance before it can be approved.",
        os: "all",
        stepLinks: [
          { label: "Submit an Access Request Form", url: "https://ithelp.protegrity.com/app/itdesk/ui/requests/add?reqTemplate=98954000032782457" }
        ]
      }
    ],
    notes: [
      "✅ Anything listed in Company Portal or Iru Self-Service is already approved — install it freely, no ticket needed",
      "🔑 You never need an admin password to install from the self-service portal",
      "📋 Not in the self-service portal? Check the Approved or Denied Applications list before raising a ticket",
      "🔐 Prompted for an administrator password? Raise a CyberArk admin privilege request",
      "🎫 Not on the approved list at all? Submit an Access Request Form with your business reason",
      "🚫 The Microsoft Store and Mac App Store are blocked — software must come from the self-service portal or the approved list",
      "⛔ Never install software that is not in the self-service portal or on the approved list"
    ],
    items: [
      { id: "14-1", text: "Show Company Portal and how to find it", os: "win" },
      { id: "14-2", text: "Demonstrate installing an app", os: "win" },
      { id: "14-3", text: "Show the Iru Self-Service app and how to find it", os: "mac" },
      { id: "14-4", text: "Demonstrate installing an app", os: "mac" },
      { id: "14-5", text: "Explain how to request an app that is not listed", os: "all" },
    ],
    links: [
      { label: "Approved software list — Windows", url: "https://protegrity.sharepoint.com/sites/IT/Lists/Windows%20Software%20Inventory/AllItems.aspx", os: "win" },
      { label: "Approved software list — macOS", url: "https://protegrity.sharepoint.com/sites/IT/Lists/MacOS%20Software%20Inventory/All%20Software.aspx", os: "mac" },
      { label: "Request CyberArk admin privileges", url: "https://ithelp.protegrity.com/app/itdesk/ui/requests/add?reqTemplate=98954000047393431", os: "all" },
      { label: "Submit an Access Request Form", url: "https://ithelp.protegrity.com/app/itdesk/ui/requests/add?reqTemplate=98954000032782457", os: "all" },
    ]
  },

  // ===========================================================================
  // SECTION 15 — Privileged Access
  // ===========================================================================
  {
    id: 15,
    title: "Privileged Access",
    icon: "🛡️",
    agentScript: "Next, let's talk about privileged access on your laptop. You are set up as a standard user rather than a permanent administrator. I'll explain why, and how to get temporary admin rights when you genuinely need them.",
    mainLink: null,
    steps: [
      {
        id: "15-s1",
        text: "You have standard user permissions, not permanent admin access",
        detail: "On your Protegrity-issued laptop you are set up with standard user permissions rather than permanent administrator access. This reduces the risk of malware, accidental system changes, and unauthorized software installations.",
        os: "all"
      },
      {
        id: "15-s2",
        text: "Temporary admin access is available through CyberArk EPM",
        detail: "If administrative access is required, Protegrity provides temporary, time-limited admin access through CyberArk EPM, which securely manages privileged access. Raise a CyberArk Admin Privilege Request and attach a screenshot of the CyberArk notification message for the quickest response.",
        os: "all",
        stepLinks: [
          { label: "Raise a CyberArk Admin Privilege Request", url: "https://ithelp.protegrity.com/app/itdesk/ui/requests/add?reqTemplate=98954000047393431" }
        ],
        rulesLabel: "CyberArk EPM controls access to:",
        rules: [
          "Software installations",
          "Privileged operating system actions"
        ]
      },
      {
        id: "15-s3",
        text: "All software must be reviewed before use",
        detail: "Any unvetted free or paid (licensed) software must be reviewed before use. This ensures security, legal, and licensing compliance before procurement or installation. To request an application that has not yet been approved, submit an Access Request Form.",
        os: "all",
        stepLinks: [
          { label: "Submit an Access Request Form", url: "https://ithelp.protegrity.com/app/itdesk/ui/requests/add?reqTemplate=98954000032782457" }
        ]
      }
    ],
    notes: [
      "🛡️ Standard user access protects both you and Protegrity customer data",
      "⏱️ CyberArk EPM admin rights are time-limited, not permanent",
      "📝 Need admin rights to install or run something? Raise a CyberArk Admin Privilege Request",
      "📨 Need software that has not been approved yet? Submit an Access Request Form"
    ],
    items: [
      { id: "15-1", text: "Explain standard user permissions", os: "all" },
      { id: "15-2", text: "Explain CyberArk EPM temporary admin access", os: "all" },
      { id: "15-3", text: "Explain software review requirement", os: "all" },
    ],
    links: [
      { label: "Raise a CyberArk Admin Privilege Request", url: "https://ithelp.protegrity.com/app/itdesk/ui/requests/add?reqTemplate=98954000047393431" },
      { label: "Submit an Access Request Form", url: "https://ithelp.protegrity.com/app/itdesk/ui/requests/add?reqTemplate=98954000032782457" },
    ]
  },

  // ===========================================================================
  // SECTION 16 — Security Awareness
  // ===========================================================================
  {
    id: 16,
    title: "Security Awareness",
    icon: "🔍",
    agentScript: "Before we wrap up, let's cover security awareness. I'll explain how to report suspicious emails, and why some of our security controls may feel stricter than what you've seen elsewhere.",
    mainLink: null,
    steps: [
      {
        id: "16-s1",
        text: "Report suspicious or phishing emails using the Report Phish add-in",
        detail: "Suspicious or phishing emails should be reported using the Report Phish add-in in Outlook. This sends the message directly to the Information Security team for analysis and helps protect other users.",
        os: "all"
      },
      {
        id: "16-s2",
        text: "Protegrity uses a risk-based approach to security",
        detail: "We balance employee productivity with customer, regulatory, and contractual security requirements. Some controls may feel more restrictive than at previous companies, due to customer compliance requirements — especially from regulated financial institutions.",
        os: "all"
      },
      {
        id: "16-s3",
        text: "Some tools, applications and websites may be blocked",
        detail: "Certain tools, applications, or websites may be blocked or restricted due to security risk, licensing, or customer compliance requirements. Access can still be requested if there is a valid business need and the tool passes security review.",
        os: "all"
      }
    ],
    notes: [
      "🎣 When in doubt about an email, report it — never click the links",
      "🏦 Stricter controls come from regulated customer requirements, not distrust",
      "✅ Blocked does not mean banned — raise a request with a business justification"
    ],
    items: [
      { id: "16-1", text: "Show Report Phish add-in in Outlook", os: "all" },
      { id: "16-2", text: "Explain risk-based security approach", os: "all" },
      { id: "16-3", text: "Explain how to request blocked tools", os: "all" },
    ],
    links: [
      { label: "Contact Information Security", url: "mailto:corp.infosec@protegrity.com" },
    ]
  },

  {
    id: 17,
    title: "Wrap Up",
    icon: "✅",
    agentScript: "That's everything we needed to cover today. Do you have any questions before we finish? Anything you ask will be written down and included in your summary email, and the IT team will follow up with you directly. I'll also send you all the helpful links we covered.",
    mainLink: null,
    steps: [
      { id: "17-s1", text: "Confirm you can sign in to Outlook with MFA", detail: "Test that everything works end-to-end", os: "all" },
      {
        id: "17-s2",
        text: "Share any questions you still have",
        detail: "If you have any questions beyond the topics we covered today, or anything is still unclear, please tell us now. We will record them using the Add follow-up note button at the bottom of this section. Your questions will be included in your summary email and a member of the IT team will follow up with you directly.",
        os: "all"
      },
      { id: "17-s3", text: "Review what we covered today", detail: "All sections will be in your summary email", os: "all" }
    ],
    notes: [
      "📧 Summary email will be sent shortly after this call",
      "📝 Any questions raised today are recorded and sent to IT with your summary email",
      "🆘 Questions after today? Raise a ticket at ithelp.protegrity.com — no question is too small",
      "🔐 Security questions can go directly to corp.infosec@protegrity.com",
      "🎉 Welcome to Protegrity!"
    ],
    items: [
      { id: "17-1", text: "Confirm all questions answered", os: "all" },
      { id: "17-2", text: "Send summary email", os: "all" },
    ],
    links: []
  }
];
